import { getFullAddress } from "@caprikorn/dynamo-utils";
import { generatePath } from "react-router-dom";

import { ROUTES } from "@router/router.constants";
import type {
  PostBillConsolidationsFilter,
  PostPointsOfDeliveryFilter,
} from "@services/apiEcms/apiEcms.types";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import type {
  DataType,
  LevelTypes,
  ParsedSearchResults,
  References,
} from "./SearchDrawer.types";

const formatReferences = ({ dataConso, dataPod }: DataType): References => {
  const data = dataPod || dataConso;

  return {
    entity: data.childEntity.reference,
    billConsolidation: data.billConsolidation.reference,
    pointOfDelivery: dataPod?.number,
    energyType:
      dataPod && (["GAS", "GAZ"].includes(dataPod.energy) ? "GAS" : "ELEC"),
  };
};

export const useParseBillConsolidationResponse = (
  data: PostBillConsolidationsFilter | undefined,
): ParsedSearchResults | undefined => {
  const { t } = useCustomTranslation("main");

  if (!data) return undefined;

  const result = data.billConsolidations.flatMap((item) => ({
    name: item.billConsolidation.name,
    references: formatReferences({ dataConso: item }),
    data: [
      {
        name: t("searchDrawer.result.billConsolidations.entity"),
        value: item.childEntity.name,
        subtitle: item.childEntity.reference,
      },
      {
        name: t("searchDrawer.result.billConsolidations.billingAddress"),
        value: getFullAddress({
          street: item.address.streetName,
          streetNumber: item.address.streetNumber,
        }),
        subtitle: item.address.municipality
          ? `${item.address.municipality} (${item.address?.zipCode})`
          : "",
      },
      {
        name: t("searchDrawer.result.billConsolidations.billingContact"),
        subtitle: item.email,
        value:
          item?.firstName && item?.lastName
            ? `${item.firstName} ${item.lastName}`
            : "",
      },
    ],
  }));
  return { pagination: data.pagination, itemList: result };
};

export const useParsePointsOfDeliveryResponse = (
  data: PostPointsOfDeliveryFilter | undefined,
): ParsedSearchResults | undefined => {
  const { t } = useCustomTranslation("main");

  if (!data) return undefined;

  const result = data.pointsOfDelivery.flatMap((item) => ({
    name: item.billingName,
    references: formatReferences({ dataPod: item }),
    data: [
      {
        value: item.childEntity.name,
        name: t("searchDrawer.result.pointOfDelivery.entity"),
        subtitle: item.childEntity.reference,
      },
      {
        value: item.billConsolidation.name,
        name: t("searchDrawer.result.pointOfDelivery.billConsolidation"),
        subtitle: item.billConsolidation.reference,
      },
      {
        name: t("searchDrawer.result.pointOfDelivery.consumptionAddress"),
        value: getFullAddress({
          street: item.address.streetName,
          streetNumber: item.address.streetNumber,
        }),
        subtitle: item.address.municipality
          ? `${item.address.municipality} (${item.address?.zipCode})`
          : "",
      },
    ],
  }));

  return {
    pagination: data.pagination,
    itemList: result,
  };
};

export const computeRoute = ({
  refs,
  level,
}: {
  refs: References;
  level: LevelTypes;
}) => {
  const isSiteLevel = level === "pointOfDelivery";
  const route = isSiteLevel
    ? ROUTES.PERIMETER_SITE
    : ROUTES.PERIMETER_SITES_BY_CONTRACT;

  return generatePath(route, {
    customerId: refs.entity ?? "",
    contractId: refs.billConsolidation ?? "",
    siteId: refs.pointOfDelivery ?? "",
    energy: refs.energyType ?? "",
  });
};
